import { HubConnection } from '@microsoft/signalr';
import { useEffect } from 'react';
import logger from 'services/Logging/logService';
import { CopilotAgentEvent, ReceiveAgentEvent, SendEditorToolResult } from 'services/SignalR/CopilotHub';

interface IUseEditorCommandBridgeOptions {
  hubConnection?: HubConnection;
  isCommand: (event: CopilotAgentEvent) => boolean;
  executeCommand: (event: CopilotAgentEvent, args: Record<string, unknown>) => Promise<unknown> | unknown;
  getResultMethod?: (event: CopilotAgentEvent) => string;
  logPrefix: string;
}

const parseCommandArgs = (event: CopilotAgentEvent, logPrefix: string): Record<string, unknown> => {
  try {
    return event.argumentsJson ? (JSON.parse(event.argumentsJson) as Record<string, unknown>) : {};
  } catch (err) {
    logger.debug(`${logPrefix}: failed to parse command args`, err);

    return {};
  }
};

export const useEditorCommandBridge = (options: IUseEditorCommandBridgeOptions): void => {
  const { hubConnection, isCommand, executeCommand, getResultMethod, logPrefix } = options;

  useEffect(() => {
    if (!hubConnection) return undefined;

    const handler = async (event: CopilotAgentEvent) => {
      if (!isCommand(event) || !event.callId) return;

      let result: unknown;
      try {
        result = await Promise.resolve(executeCommand(event, parseCommandArgs(event, logPrefix)));
      } catch (err) {
        logger.debug(`${logPrefix}: command failed`, err);
        result = { error: 'The editor command could not be executed.' };
      }

      const method = getResultMethod?.(event) ?? SendEditorToolResult;
      hubConnection.invoke(method, event.callId, JSON.stringify(result)).catch((err) => {
        logger.debug(`${logPrefix}: failed to send editor command result`, err);
      });
    };

    hubConnection.on(ReceiveAgentEvent, handler);

    return () => {
      hubConnection.off(ReceiveAgentEvent, handler);
    };
  }, [executeCommand, getResultMethod, hubConnection, isCommand, logPrefix]);
};
